/* ============================================================================
   draxon.js — shared Draxon front-end module
   Loaded `defer` in theme.liquid. Dependency-free, reduced-motion aware.
   Holds the cross-cutting interaction layer used by every custom section:
   scroll reveals + stagger, the analytics event helper, and (added per
   section) the engagement-capture form, hotspots, fit slider, etc.
   ============================================================================ */
(function () {
  'use strict';

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- Scroll reveal: .drx-reveal -> .is-visible, with optional stagger ----
     Add data-drx-stagger to a container to delay its .drx-reveal children in
     sequence (CSS reads --drx-i). Reveals fire once, then unobserve. */
  function initReveals() {
    document.querySelectorAll('[data-drx-stagger]').forEach(function (group) {
      group.querySelectorAll('.drx-reveal').forEach(function (k, i) {
        k.style.setProperty('--drx-i', i);
      });
    });

    var els = document.querySelectorAll('.drx-reveal, .drx-blade');
    if (!els.length) return;

    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach(function (el) {
        el.classList.add('is-visible');
      });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    els.forEach(function (el) {
      io.observe(el);
    });

    /* Safety net: under `defer` the script runs pre-paint, and on a heavy page
       (e.g. the collection grid) the observer's initial callback can be starved
       or missed — leaving above-the-fold content stuck at opacity:0, which looks
       like the page never finished loading. Force-reveal anything in or above the
       viewport shortly after init and again on load; below-the-fold elements keep
       their scroll-triggered animation. */
    function revealInView() {
      var vh = window.innerHeight || document.documentElement.clientHeight || 0;
      var remaining = false;
      els.forEach(function (el) {
        if (el.classList.contains('is-visible')) return;
        if (el.getBoundingClientRect().top < vh * 0.92) {
          el.classList.add('is-visible');
          io.unobserve(el);
        } else {
          remaining = true;
        }
      });
      return remaining;
    }
    /* Drive the reveal from scroll too, so a long page (e.g. the Story page)
       reveals every section on scroll even if the observer is non-functional.
       Detaches itself once everything has been revealed. */
    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!revealInView()) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        }
      });
    }
    setTimeout(revealInView, 400);
    window.addEventListener('load', function () {
      setTimeout(revealInView, 50);
    });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
  }

  /* ---- Analytics: fire a Rybbit custom event if Rybbit is present ----
     No-ops cleanly when Rybbit is not installed (e.g. dev store). */
  window.drxTrack = function (name, props) {
    try {
      var r = window.rybbit;
      if (!r) return;
      if (typeof r.event === 'function') r.event(name, props || {});
      else if (typeof r.trackEvent === 'function')
        r.trackEvent(name, props || {});
    } catch (e) {
      /* swallow — analytics must never break the page */
    }
  };

  function init() {
    initReveals();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
   Notify-me modal — product engagement capture (The Drop). Opened by any
   [data-drx-notify] trigger; tags the capture notify:{handle}; AJAX-submits
   Shopify's customer form. Reused as the engagement capture by later sections.
   ============================================================================ */
(function () {
  'use strict';
  var modal = document.getElementById('drx-notify');
  if (!modal) return;

  var productEl = modal.querySelector('[data-drx-notify-product]');
  var tagInput = modal.querySelector('[data-drx-notify-tag]');
  var form = modal.querySelector('#drx-notify-form');
  var email = modal.querySelector('#drx-notify-email');
  var okMsg = modal.querySelector('[data-drx-notify-ok]');
  var errMsg = modal.querySelector('[data-drx-notify-err]');
  var lastTrigger = null;

  function open(name, handle, heading, desc) {
    var titleEl = modal.querySelector('#drx-notify-title');
    var descEl = modal.querySelector('.drx-np__desc');
    if (titleEl) titleEl.textContent = heading || 'First access.';
    if (descEl)
      descEl.textContent =
        desc || 'Be first when ' + (name || 'this piece') + ' drops. No spam.';
    if (tagInput) tagInput.value = handle ? 'notify:' + handle : 'notify';
    if (okMsg) okMsg.hidden = true;
    if (errMsg) errMsg.hidden = true;
    if (form) form.style.display = '';
    modal.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (email) {
      try {
        email.focus();
      } catch (e) {}
    }
    window.drxTrack('notify_me_click', { product: handle || name || '' });
  }

  function close() {
    modal.hidden = true;
    document.documentElement.style.overflow = '';
    if (lastTrigger) {
      try {
        lastTrigger.focus();
      } catch (e) {}
    }
  }

  // Expose so interactive sections (hotspots, fit slider, scrolly) can surface
  // the same capture as the engagement capture.
  window.drxNotify = { open: open, close: close };

  document.addEventListener('click', function (e) {
    var trigger = e.target.closest('[data-drx-notify]');
    if (trigger) {
      e.preventDefault();
      lastTrigger = trigger;
      open(
        trigger.getAttribute('data-product'),
        trigger.getAttribute('data-handle'),
        trigger.getAttribute('data-heading'),
        trigger.getAttribute('data-desc'),
      );
      return;
    }
    if (e.target.closest('[data-drx-notify-close]')) close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && !modal.hidden) close();
  });

  // Submit NATIVELY — do not AJAX this form. Shopify's spam protection
  // (reCAPTCHA) is enforced server-side on /contact and cannot be satisfied by
  // a background fetch: the fetch fails, the inline "Something went wrong"
  // error flashes, and Shopify forces its own full-page challenge regardless.
  // A native POST lets Shopify's invisible reCAPTCHA clear real visitors
  // silently (most never see a challenge); the notify:{handle} tag set in
  // open() rides along in the POST, and the confirmation shows on return via
  // the ?customer_posted=true handler below. (okMsg/errMsg stay in the markup
  // but are no longer toggled by JS.)
})();

/* ============================================================================
   Fabric hotspots — tap/keyboard toggle (desktop also uses CSS hover). After
   two distinct pins are opened by click/tap, surface the first-access capture
   once (engagement capture, reuses the notify modal).
   ============================================================================ */
(function () {
  'use strict';
  var sections = document.querySelectorAll('[data-drx-hotspots]');
  if (!sections.length) return;

  sections.forEach(function (section) {
    var pins = section.querySelectorAll('.drx-hotspot-pin');
    if (!pins.length) return;
    var opened = {};
    var captureFired = false;

    function closeAll(except) {
      pins.forEach(function (p) {
        if (p !== except) p.setAttribute('aria-expanded', 'false');
      });
    }

    function toggle(pin) {
      var isOpen = pin.getAttribute('aria-expanded') === 'true';
      closeAll(pin);
      pin.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      if (isOpen) return;

      var label = pin.getAttribute('aria-label') || '';
      if (!opened[label]) {
        opened[label] = true;
        window.drxTrack('hotspot_open', { label: label });
      }
      if (
        !captureFired &&
        Object.keys(opened).length >= 2 &&
        window.drxNotify
      ) {
        captureFired = true;
        window.drxTrack('hotspots_engaged', {});
        setTimeout(function () {
          window.drxNotify.open('the collection', 'collection');
        }, 600);
      }
    }

    pins.forEach(function (pin) {
      pin.addEventListener('click', function (e) {
        if (e.target.closest('.drx-hotspot-shop')) return; // let the link work
        e.preventDefault();
        toggle(pin);
      });
      pin.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggle(pin);
        } else if (e.key === 'Escape') {
          pin.setAttribute('aria-expanded', 'false');
        }
      });
    });

    document.addEventListener('click', function (e) {
      if (!e.target.closest('.drx-hotspot-pin')) closeAll(null);
    });
  });
})();

/* ============================================================================
   Fabric deep-dive (section 5) — scrollytelling. IntersectionObserver marks the
   centered attribute step active and drives the sticky panel (icon + name); the
   SVG weave knits in once the panel enters view.
   ============================================================================ */
(function () {
  'use strict';
  var section = document.querySelector('.drx-fd');
  if (!section) return;
  var sticky = section.querySelector('.drx-fd__sticky');
  var nameEl = section.querySelector('[data-fd-name]');
  var icons = sticky
    ? Array.prototype.slice.call(sticky.querySelectorAll('.drx-fd__icon'))
    : [];
  var steps = Array.prototype.slice.call(
    section.querySelectorAll('.drx-fd__step'),
  );
  if (!steps.length) return;

  function setActive(step) {
    var idx = parseInt(step.getAttribute('data-index'), 10) || 0;
    if (nameEl) nameEl.textContent = step.getAttribute('data-name') || '';
    icons.forEach(function (ic, i) {
      ic.classList.toggle('is-active', i === idx);
    });
    steps.forEach(function (s) {
      s.classList.toggle('is-current', s === step);
    });
  }
  setActive(steps[0]);

  if (!('IntersectionObserver' in window)) {
    if (sticky) sticky.classList.add('is-knit');
    return;
  }

  var io = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          setActive(e.target);
          window.drxTrack('fabric_step_view', {
            step: e.target.getAttribute('data-index'),
          });
        }
      });
    },
    { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
  );
  steps.forEach(function (s) {
    io.observe(s);
  });

  if (sticky) {
    var knitIo = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            sticky.classList.add('is-knit');
            knitIo.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2 },
    );
    knitIo.observe(sticky);
  }
})();

/* ============================================================================
   Fit comparison slider (section 6) — At rest <-> In motion. The media is the
   pointer drag surface (mouse + touch, drag/click anywhere); a hidden range
   carries keyboard a11y. Both drive --pos. One-time auto-peek signals it's
   draggable (B=MAP prompt). Rybbit fit_slider_drag on first interaction.
   ============================================================================ */
(function () {
  'use strict';
  var comp = document.querySelector('[data-drx-compare]');
  if (!comp) return;
  var media = comp.querySelector('.drx-fit__media');
  var range = comp.querySelector('[data-drx-compare-range]');
  if (!media) return;
  var touched = false;

  function clamp(v) {
    return Math.max(0, Math.min(100, v));
  }
  function setPos(v) {
    v = clamp(v);
    media.style.setProperty('--pos', v + '%');
    if (range && Number(range.value) !== Math.round(v))
      range.value = Math.round(v);
  }
  function markTouched() {
    if (touched) return;
    touched = true;
    comp.classList.add('is-touched');
    window.drxTrack('fit_slider_drag', {});
  }
  function fromX(clientX) {
    var r = media.getBoundingClientRect();
    setPos(((clientX - r.left) / r.width) * 100);
  }

  setPos(Number(range && range.value) || 50);

  var dragging = false;
  media.addEventListener('pointerdown', function (e) {
    dragging = true;
    try {
      media.setPointerCapture(e.pointerId);
    } catch (err) {}
    fromX(e.clientX);
    markTouched();
  });
  media.addEventListener('pointermove', function (e) {
    if (dragging) fromX(e.clientX);
  });
  function endDrag() {
    dragging = false;
  }
  media.addEventListener('pointerup', endDrag);
  media.addEventListener('pointercancel', endDrag);

  if (range) {
    range.addEventListener('input', function () {
      setPos(Number(range.value));
      markTouched();
    });
  }

  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!reduce && 'IntersectionObserver' in window) {
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (!e.isIntersecting || touched) return;
          io.unobserve(e.target);
          var seq = [50, 70, 32, 50],
            i = 0;
          var t = setInterval(function () {
            if (touched || i >= seq.length) {
              clearInterval(t);
              return;
            }
            setPos(seq[i]);
            i++;
          }, 440);
        });
      },
      { threshold: 0.55 },
    );
    io.observe(comp);
  }
})();

/* ============================================================================
   First-access band (section 8) — submits NATIVELY to Shopify's customer
   endpoint. Deliberately NOT AJAX-submitted: Shopify's server-side reCAPTCHA
   can't be satisfied by a background fetch, so the old fetch only flashed a
   false "Something went wrong" error before Shopify's own full-page challenge
   took over. A native POST lets the invisible reCAPTCHA clear real visitors
   silently; the confirmation shows on return via the ?customer_posted=true
   handler below. (data-drx-news-ok / -err stay in the markup but are unused.)
   ============================================================================ */

/* ============================================================================
   Signup confirmation on return. Shopify's customer form redirects back with
   ?customer_posted=true after a successful submit — INCLUDING after the
   spam-challenge (captcha) page, where the inline AJAX message never renders
   because that path is a full-page navigation, not an in-page fetch. So the
   newsletter band + the "Notify me" modal had no visible confirmation once a
   submit went through the captcha. Surface one branded confirmation here, then
   strip the param so a refresh or Back doesn't repeat it. (The contact form
   keeps its own server-side ?contact_posted message, so it is left alone.)
   ============================================================================ */
(function () {
  'use strict';
  var params = new URLSearchParams(window.location.search);
  if (params.get('customer_posted') !== 'true') return;

  params.delete('customer_posted');
  var qs = params.toString();
  var clean =
    window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
  if (window.history && window.history.replaceState) {
    window.history.replaceState({}, document.title, clean);
  }

  function build() {
    if (document.querySelector('.drx-confirm')) return;
    var banner = document.createElement('div');
    banner.className = 'drx-confirm';
    banner.setAttribute('role', 'status');
    banner.setAttribute('aria-live', 'polite');
    banner.innerHTML =
      '<div class="drx-confirm__inner">' +
      '<span class="drx-confirm__msg">Thanks — you’re on the list for first access to the Fall 2026 drop.</span>' +
      '<button type="button" class="drx-confirm__close" aria-label="Dismiss">' +
      '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
      '</button>' +
      '</div>';
    document.body.appendChild(banner);
    // Force a reflow so the slide-in transition plays deterministically, even
    // when requestAnimationFrame is paused (e.g. the tab is briefly backgrounded
    // right after the submit). rAF can defer indefinitely on a hidden tab.
    void banner.offsetWidth;
    banner.classList.add('is-in');

    var timer = setTimeout(dismiss, 8000);
    function dismiss() {
      clearTimeout(timer);
      banner.classList.remove('is-in');
      setTimeout(function () {
        if (banner.parentNode) banner.parentNode.removeChild(banner);
      }, 450);
    }
    banner
      .querySelector('.drx-confirm__close')
      .addEventListener('click', dismiss);
    if (window.drxTrack)
      window.drxTrack('capture_confirmed', { via: 'return' });
  }

  if (document.body) build();
  else document.addEventListener('DOMContentLoaded', build);
})();

/* ============================================================================
   Magnetic buttons ([data-magnetic]) — the element drifts slightly toward the
   pointer. Hover-capable pointers only; skipped under reduced motion.
   ============================================================================ */
(function () {
  'use strict';
  var els = document.querySelectorAll('[data-magnetic]');
  if (!els.length) return;
  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) return;

  els.forEach(function (el) {
    el.addEventListener('pointermove', function (e) {
      if (e.pointerType === 'touch') return;
      var r = el.getBoundingClientRect();
      var mx = e.clientX - (r.left + r.width / 2);
      var my = e.clientY - (r.top + r.height / 2);
      el.style.transform = 'translate(' + mx * 0.25 + 'px,' + my * 0.4 + 'px)';
    });
    el.addEventListener('pointerleave', function () {
      el.style.transform = '';
    });
  });
})();

/* ---- Collection sort — navigate on change, preserving any active filters ---- */
(function () {
  document.querySelectorAll('[data-drx-sort] select').forEach(function (sel) {
    sel.addEventListener('change', function () {
      var url = new URL(window.location.href);
      url.searchParams.set('sort_by', sel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  });
})();

/* ---- Filter drawer (mobile) open/close ---- */
(function () {
  function lock(on) {
    document.documentElement.style.overflow = on ? 'hidden' : '';
  }
  function closeAll() {
    document.querySelectorAll('[data-drx-drawer]').forEach(function (d) {
      d.classList.remove('is-open');
    });
    var ov = document.querySelector('[data-drx-drawer-overlay]');
    if (ov) {
      ov.classList.remove('is-open');
      setTimeout(function () {
        ov.hidden = true;
      }, 260);
    }
    document.querySelectorAll('[data-drx-drawer-open]').forEach(function (b) {
      b.setAttribute('aria-expanded', 'false');
    });
    lock(false);
  }
  document.querySelectorAll('[data-drx-drawer-open]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var id = btn.getAttribute('aria-controls');
      var drawer = id
        ? document.getElementById(id)
        : document.querySelector('[data-drx-drawer]');
      var ov = document.querySelector('[data-drx-drawer-overlay]');
      if (drawer) drawer.classList.add('is-open');
      if (ov) {
        ov.hidden = false;
        requestAnimationFrame(function () {
          ov.classList.add('is-open');
        });
      }
      btn.setAttribute('aria-expanded', 'true');
      lock(true);
    });
  });
  document
    .querySelectorAll('[data-drx-drawer-close], [data-drx-drawer-overlay]')
    .forEach(function (el) {
      el.addEventListener('click', closeAll);
    });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeAll();
  });
})();

/* ---- AJAX facet filtering — swap grid + count + facets in place ---- */
(function () {
  var section = document.querySelector('.drx-collection');
  if (!section || !window.fetch || !window.DOMParser) return;

  function apply(html, url) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var newMain = doc.querySelector('.drx-collection__main');
    var curMain = section.querySelector('.drx-collection__main');
    if (newMain && curMain) {
      newMain.querySelectorAll('.drx-reveal').forEach(function (el) {
        el.classList.add('is-visible');
      });
      curMain.replaceWith(newMain);
    }
    var newCount = doc.querySelector('.drx-collection__count');
    var curCount = section.querySelector('.drx-collection__count');
    if (newCount && curCount) curCount.innerHTML = newCount.innerHTML;
    var newForm = doc.querySelector('[data-drx-facet-form]');
    var curForm = section.querySelector('[data-drx-facet-form]');
    if (newForm && curForm) curForm.innerHTML = newForm.innerHTML;
    window.history.pushState({ drx: true }, '', url);
  }

  function go(url) {
    section.classList.add('is-loading');
    fetch(url, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        apply(html, url);
        section.classList.remove('is-loading');
      })
      .catch(function () {
        window.location.href = url;
      });
  }

  section.addEventListener('change', function (e) {
    var form = e.target.closest('[data-drx-facet-form]');
    if (!form) return;
    var params = new URLSearchParams(new FormData(form));
    var clean = new URLSearchParams();
    params.forEach(function (v, k) {
      if (v !== '') clean.append(k, v);
    });
    var qs = clean.toString();
    go(form.action.split('?')[0] + (qs ? '?' + qs : ''));
  });

  section.addEventListener('click', function (e) {
    var link = e.target.closest('[data-drx-facet-link]');
    if (!link) return;
    e.preventDefault();
    go(link.href);
  });

  window.addEventListener('popstate', function () {
    go(window.location.href);
  });
})();

/* ---- PDP variant selection — update price/availability + fold the chosen
   variant into the Notify capture (pre-launch: no cart) ---- */
(function () {
  var pdp = document.querySelector('[data-drx-pdp]');
  if (!pdp) return;
  var dataEl = pdp.querySelector('[data-drx-variants]');
  var variants = [];
  try {
    variants = JSON.parse(dataEl.textContent);
  } catch (e) {}
  var priceEl = pdp.querySelector('[data-drx-price]');
  var availEl = pdp.querySelector('[data-drx-avail]');
  var stickyPrice = pdp.querySelector('.drx-pdp__stickybar-price');
  var notifyBtns = pdp.querySelectorAll('[data-drx-notify]');
  var baseHandle = notifyBtns[0]
    ? notifyBtns[0].getAttribute('data-handle')
    : '';

  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }
  function selected() {
    var vals = [];
    pdp.querySelectorAll('[data-drx-opt]').forEach(function (fs) {
      var c = fs.querySelector('input:checked');
      if (c) vals.push(c.value);
    });
    return vals;
  }
  function update() {
    var vals = selected();
    pdp.querySelectorAll('[data-drx-opt]').forEach(function (fs) {
      var c = fs.querySelector('input:checked');
      var cur = fs.querySelector('[data-drx-opt-current]');
      if (c && cur) cur.textContent = c.value;
      fs.querySelectorAll('.drx-pdp__swatch, .drx-pdp__sizebtn').forEach(
        function (l) {
          var i = l.querySelector('input');
          l.classList.toggle('is-active', !!(i && i.checked));
        },
      );
    });
    var v = variants.filter(function (x) {
      return (x.options || []).join('~') === vals.join('~');
    })[0];
    if (v) {
      if (priceEl) priceEl.textContent = money(v.price);
      if (stickyPrice) stickyPrice.textContent = money(v.price);
      if (availEl) {
        availEl.innerHTML =
          '<span class="drx-pdp__avail-dot' +
          (v.available ? ' is-in' : '') +
          '" aria-hidden="true"></span>' +
          (v.available ? 'In stock' : 'Launching Fall 2026 — get first access');
      }
    }
    var tag = baseHandle + (vals.length ? ':' + vals.join('-') : '');
    notifyBtns.forEach(function (b) {
      b.setAttribute('data-handle', tag);
    });
  }
  pdp.addEventListener('change', function (e) {
    if (
      e.target &&
      e.target.hasAttribute &&
      e.target.hasAttribute('data-drx-opt-input')
    )
      update();
  });
  update();
})();

/* ---- PDP sticky Notify bar — show once the main button scrolls above view ---- */
(function () {
  var bar = document.querySelector('[data-drx-pdp-bar]');
  var anchor = document.querySelector('[data-drx-pdp-notify]');
  if (!bar || !anchor || !('IntersectionObserver' in window)) return;
  bar.hidden = false;
  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      var passed = e.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', !e.isIntersecting && passed);
    });
  });
  io.observe(anchor);
})();

/* ---- PDP gallery zoom — fullscreen lightbox ---- */
(function () {
  var pdp = document.querySelector('[data-drx-pdp]');
  if (!pdp) return;
  var shots = pdp.querySelectorAll('[data-drx-zoom]');
  if (!shots.length) return;
  var overlay;
  function close() {
    if (overlay) {
      overlay.classList.remove('is-open');
      document.documentElement.style.overflow = '';
    }
  }
  function open(src, alt) {
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'drx-pdp__zoom';
      overlay.innerHTML =
        '<button class="drx-pdp__zoom-close" type="button" aria-label="Close">×</button><img alt="">';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', function (e) {
        if (e.target === overlay || e.target.closest('.drx-pdp__zoom-close'))
          close();
      });
    }
    var img = overlay.querySelector('img');
    img.src = src;
    img.alt = alt || '';
    overlay.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }
  shots.forEach(function (s) {
    s.addEventListener('click', function () {
      var im = s.querySelector('img');
      if (im) open(im.currentSrc || im.src, im.alt);
    });
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') close();
  });
})();

/* ---- Background crossfade slider ([data-drx-slider]) — auto-advancing ---- */
(function () {
  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  document.querySelectorAll('[data-drx-slider]').forEach(function (slider) {
    var slides = slider.querySelectorAll('.drx-fhero__slide');
    if (slides.length < 2) return;
    if (!slider.querySelector('.drx-fhero__slide.is-active')) {
      slides[0].classList.add('is-active');
    }
    if (reduce) return;
    var i = 0;
    setInterval(function () {
      slides[i].classList.remove('is-active');
      i = (i + 1) % slides.length;
      slides[i].classList.add('is-active');
    }, 5500);
  });
})();

/* ---- Fabric "how it breathes" panel ---------------------------------------
   Spawns cool-air-in + heat/moisture-out motes into [data-drx-breathe] and
   wires each fabric layer's hover/focus/click to the readout caption.
   Reduced-motion: no motes, no spawn — the static cross-section still reads. */
(function () {
  var panels = document.querySelectorAll('[data-drx-breathe]');
  if (!panels.length) return;
  var reduce =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  panels.forEach(function (panel) {
    var deep = panel.querySelector('.drx-breathe__motes--deep');
    var near = panel.querySelector('.drx-breathe__motes--near');
    if ((deep || near) && !reduce) {
      var make = function (target, cls, n) {
        if (!target) return;
        for (var i = 0; i < n; i++) {
          var m = document.createElement('span');
          m.className = 'drx-breathe__mote ' + cls;
          m.style.top = 6 + Math.random() * 88 + '%';
          m.style.animationDuration = 4.4 + Math.random() * 3.6 + 's';
          m.style.animationDelay = -(Math.random() * 7).toFixed(2) + 's';
          m.style.setProperty(
            '--drx-drift',
            (Math.random() * 26 - 13).toFixed(1) + 'px',
          );
          target.appendChild(m);
        }
      };
      // Deep set rides BEHIND the mid + front knit (shows through the pores);
      // near set floats in front — together they read as air through the weave.
      make(deep, 'drx-breathe__mote--in', 13);
      make(deep, 'drx-breathe__mote--out', 11);
      make(near, 'drx-breathe__mote--in', 6);
      make(near, 'drx-breathe__mote--out', 5);
    }

    // Attribute markers -> a cursor-following tooltip (perf-icon + benefit).
    var pins = panel.querySelectorAll('.drx-breathe__pin');
    var tip = panel.querySelector('.drx-breathe__tip');
    var frame = panel.querySelector('.drx-breathe__frame');
    if (pins.length && tip && frame) {
      var tipIco = tip.querySelector('.drx-breathe__tip-ico');
      var tipTitle = tip.querySelector('.drx-breathe__tip-title');
      var tipBenefit = tip.querySelector('.drx-breathe__tip-benefit');
      var activePin = null;

      var fill = function (pin) {
        var ico = pin.querySelector('.drx-breathe__pin-ico');
        if (tipIco) tipIco.innerHTML = ico ? ico.innerHTML : '';
        if (tipTitle)
          tipTitle.textContent = pin.getAttribute('data-title') || '';
        if (tipBenefit)
          tipBenefit.textContent = pin.getAttribute('data-benefit') || '';
      };

      // Place the tooltip near (x, y) — coords relative to .drx-breathe —
      // clamped inside the card; flips left if it would overflow the right edge.
      var place = function (x, y) {
        var br = panel.getBoundingClientRect();
        var fr = frame.getBoundingClientRect();
        var tw = tip.offsetWidth || 220;
        var th = tip.offsetHeight || 64;
        var minL = fr.left - br.left;
        var maxL = fr.right - br.left - tw;
        var minT = fr.top - br.top;
        var maxT = fr.bottom - br.top - th;
        var left = x + 16;
        if (left > maxL) left = x - tw - 16;
        if (left < minL) left = minL + 4;
        if (left > maxL) left = maxL;
        var top = y + 18;
        if (top > maxT) top = maxT;
        if (top < minT) top = minT;
        tip.style.left = left + 'px';
        tip.style.top = top + 'px';
      };

      var show = function (pin) {
        activePin = pin;
        fill(pin);
        tip.classList.add('is-on');
        tip.setAttribute('aria-hidden', 'false');
      };
      var hide = function () {
        activePin = null;
        tip.classList.remove('is-on');
        tip.setAttribute('aria-hidden', 'true');
      };

      pins.forEach(function (pin) {
        pin.addEventListener('mouseenter', function (e) {
          var br = panel.getBoundingClientRect();
          show(pin);
          place(e.clientX - br.left, e.clientY - br.top);
        });
        pin.addEventListener('mouseleave', hide);
        pin.addEventListener('focus', function () {
          var br = panel.getBoundingClientRect();
          var pr = pin.getBoundingClientRect();
          show(pin);
          place(
            pr.left - br.left + pr.width / 2,
            pr.top - br.top + pr.height / 2,
          );
        });
        pin.addEventListener('blur', hide);
      });

      frame.addEventListener('mousemove', function (e) {
        if (!activePin) return;
        var br = panel.getBoundingClientRect();
        place(e.clientX - br.left, e.clientY - br.top);
      });
    }
  });
})();

/* ---- Fabric "built for how you train" — discipline scrollytelling ---------
   IntersectionObserver marks the centred discipline step active; the pinned
   image cross-fades to match and the counter updates. Mirrors the homepage
   fabric deep-dive. First step active server-side so it reads with JS off. */
(function () {
  document.querySelectorAll('[data-drx-disc]').forEach(function (root) {
    var steps = Array.prototype.slice.call(
      root.querySelectorAll('.drx-disc__step'),
    );
    var shots = Array.prototype.slice.call(
      root.querySelectorAll('.drx-disc__shot'),
    );
    var tag = root.querySelector('.drx-disc__media-tag');
    if (!steps.length) return;

    // Preload every discipline image as the section approaches, so the
    // cross-fades are smooth (no decode lag on first activation).
    var imgs = Array.prototype.slice.call(
      root.querySelectorAll('.drx-disc__img'),
    );
    if ('IntersectionObserver' in window && imgs.length) {
      var pre = new IntersectionObserver(
        function (entries) {
          if (!entries[0].isIntersecting) return;
          imgs.forEach(function (im) {
            im.loading = 'eager';
            if (im.decode) im.decode().catch(function () {});
          });
          pre.disconnect();
        },
        { rootMargin: '900px 0px' },
      );
      pre.observe(root);
    }

    function pad(n) {
      return (n < 10 ? '0' : '') + n;
    }
    function setActive(step) {
      var idx = parseInt(step.getAttribute('data-index'), 10) || 0;
      steps.forEach(function (s) {
        s.classList.toggle('is-current', s === step);
      });
      shots.forEach(function (sh, i) {
        sh.classList.toggle('is-active', i === idx);
      });
      if (tag) tag.textContent = pad(idx + 1);
    }
    setActive(steps[0]);

    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) setActive(e.target);
        });
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    );
    steps.forEach(function (s) {
      io.observe(s);
    });
  });
})();

/* ---- Story "the craft" — process stepper (ARIA tabs) ----------------------
   Click or arrow/Home/End to move through the development steps; the matching
   detail panel shows. First step active server-side. */
(function () {
  document.querySelectorAll('[data-drx-steps]').forEach(function (root) {
    var tabs = Array.prototype.slice.call(
      root.querySelectorAll('[role="tab"]'),
    );
    var panels = Array.prototype.slice.call(
      root.querySelectorAll('[role="tabpanel"]'),
    );
    if (!tabs.length) return;

    function select(tab, focus) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.classList.toggle('is-active', on);
        t.tabIndex = on ? 0 : -1;
      });
      var id = tab.getAttribute('aria-controls');
      panels.forEach(function (p) {
        p.classList.toggle('is-active', p.id === id);
      });
      if (focus) tab.focus();
    }

    tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        select(tab);
      });
      tab.addEventListener('keydown', function (e) {
        var next;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown')
          next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp')
          next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End') next = tabs[tabs.length - 1];
        if (next) {
          e.preventDefault();
          select(next, true);
        }
      });
    });
  });
})();
